import type { BadgeId } from './badges';
import type { CUSTOM_THEME_COLORS } from './constants';
import type { ChannelOverwrite } from './permissions';
import type { AvatarDecorationId, NameEffectId, NameFontId, ProfileEffectId } from './profile';
import type { ReportReason, ReportStatus, StaffRole } from './staff';
/**
 * Data transfer objects shared between the API and the web client.
 * Dates are ISO-8601 strings on the wire.
 */

export type PresenceStatus = 'online' | 'idle' | 'dnd' | 'offline';
export type UserStatusPreference = 'ONLINE' | 'IDLE' | 'DND' | 'INVISIBLE';
export type ThemePreference = 'DARK' | 'LIGHT' | 'SYSTEM' | 'MIDNIGHT' | 'EMBER' | 'CUSTOM';
export type MessageDisplayPreference = 'COZY' | 'COMPACT' | 'BUBBLES';

export type CustomThemeColor = (typeof CUSTOM_THEME_COLORS)[number];

export interface CustomThemeDTO {
  /** Light or dark: decides the shadows, status colours and what the app's built-in pictures look like. */
  base: 'LIGHT' | 'DARK';
  /** #rrggbb colours. */
  colors: Record<CustomThemeColor, string>;
}
export type FriendRequestPrivacy = 'EVERYONE' | 'MUTUAL_SERVERS' | 'NOBODY';
export type DmPrivacy = 'SERVER_MEMBERS' | 'FRIENDS';
export type NotificationLevel = 'ALL' | 'MENTIONS' | 'NOTHING';
export type ChannelKind = 'TEXT' | 'VOICE' | 'FORUM';
export type NotificationKind = 'MENTION' | 'REPLY' | 'FRIEND_REQUEST' | 'FRIEND_ACCEPTED' | 'EVENT_REMINDER' | 'RAID_ALERT' | 'REMINDER';
/**
 * What a server asks of members before they can chat (and DM other members):
 * NONE nothing; LOW a confirmed email; MEDIUM also an account older than 5
 * minutes; HIGH also 10 minutes in the server. Members with a role, the owner
 * and Administrators are never held.
 */
export type SafetyLevel = 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH';
export type ConversationKind = 'DM' | 'GROUP';
/**
 * DEFAULT is something someone wrote. The others are notes about a group DM
 * change, made by the author; `content` holds the user they're about (a
 * <@id> mention) or the group's new name.
 */
export type MessageKind = 'DEFAULT' | 'GROUP_ADD' | 'GROUP_REMOVE' | 'GROUP_LEAVE' | 'GROUP_RENAME' | 'GROUP_OWNER' | 'THREAD_CREATED' | 'STOKE';

export interface PublicUser {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  accentColor: string;
  bio: string;
  customStatus: string | null;
  customStatusEmoji: string | null;
  /** When the custom status clears itself (null = never). */
  customStatusExpiresAt: string | null;
  createdAt: string;
  /* Profile customization (free for everyone). */
  /** Profile banner image (may be an animated GIF); falls back to accentColor. */
  bannerUrl: string | null;
  /** Two-colour profile theme (card background gradient). */
  themePrimary: string | null;
  themeAccent: string | null;
  avatarDecoration: AvatarDecorationId | null;
  profileEffect: ProfileEffectId | null;
  /** Display name style. No effect = plain text (role colour in servers). */
  nameFont: NameFontId | null;
  nameEffect: NameEffectId | null;
  nameColor: string | null;
  nameColor2: string | null;
  /** ClanFire staff badge (CEO / Admin / Moderator / Official), or null. */
  staffRole: StaffRole | null;
  /** Profile badges (CEO, Alpha Tester…), in display order. */
  badges: BadgeId[];
  /** When their current stretch of ClanFire Blaze began; null without Blaze. */
  blazeSince: string | null;
}

export interface SelfUser extends PublicUser {
  email: string;
  status: UserStatusPreference;
  /** Whether the owner has clicked the link in the confirmation email. */
  emailVerified: boolean;
  /** Whether logging in also needs a code from an authenticator app. */
  twoFactorEnabled: boolean;
  /** False for accounts made through Google or GitHub until they set a password. */
  hasPassword: boolean;
  /** ClanFire Blaze is on until then; null without it. */
  blazeUntil: string | null;
}

/** The answer to a correct password when the account has two-factor sign-in: send a code with this ticket. */
export interface TwoFactorChallengeDTO {
  twoFactorRequired: true;
  ticket: string;
}

export interface TwoFactorStatusDTO {
  enabled: boolean;
  enabledAt: string | null;
  /** Unused backup codes. */
  recoveryCodesLeft: number;
  /** False when the server can't store two-factor secrets (TWO_FACTOR_KEY isn't set). */
  available: boolean;
}

/** A new authenticator secret, to scan (uri, as a QR code) or type in (secret). */
export interface TwoFactorSetupDTO {
  secret: string;
  uri: string;
}

export interface TwoFactorRecoveryCodesDTO {
  /** Shown once: only their hashes are kept. */
  recoveryCodes: string[];
}

/** Where things go and how roomy they are (Settings → Appearance → Advanced). */
export interface LayoutOptionsDTO {
  /** The strip of server icons: down the left, or across the top. */
  serverList: 'LEFT' | 'TOP';
  /** Channels and DMs: left or right of the chat. */
  channelList: 'LEFT' | 'RIGHT';
  /** Whether the member list starts open on wide screens. */
  memberList: 'SHOWN' | 'HIDDEN';
  avatarSize: 'SMALL' | 'MEDIUM' | 'LARGE';
  /** Space between messages from different people. */
  spacing: 'TIGHT' | 'NORMAL' | 'ROOMY';
  /** Messages across the whole width, or in a narrower column that's easier to read. */
  chatWidth: 'FULL' | 'READABLE';
}

export interface SavedLayoutDTO {
  name: string;
  layout: LayoutOptionsDTO;
}

export interface UserSettingsDTO {
  theme: ThemePreference;
  /** Your own theme's colours (used when theme is CUSTOM), kept while you try other themes. */
  customTheme: CustomThemeDTO | null;
  messageDisplay: MessageDisplayPreference;
  fontScale: number;
  reducedMotion: boolean;
  desktopNotifications: boolean;
  notificationSounds: boolean;
  notifyOnMentions: boolean;
  notifyOnDirectMessages: boolean;
  friendRequestPrivacy: FriendRequestPrivacy;
  dmPrivacy: DmPrivacy;
  showLinkPreviews: boolean;
  layout: LayoutOptionsDTO;
  /** Layouts you've named and kept, to switch between. */
  savedLayouts: SavedLayoutDTO[];
  /** Your own CSS, applied on top of the app (only for you). */
  customCss: string;
  customCssEnabled: boolean;
}

export interface SessionDTO {
  id: string;
  userAgent: string | null;
  ipAddress: string | null;
  createdAt: string;
  lastUsedAt: string;
  current: boolean;
}

export interface RoleDTO {
  id: string;
  serverId: string;
  name: string;
  color: string | null;
  permissions: number;
  position: number;
  isDefault: boolean;
  /** Members are listed under this role in the member list. */
  hoist: boolean;
  /** Anyone can @mention it (otherwise only people who manage roles). */
  mentionable: boolean;
  /** Members can give it to themselves (Pick your roles). Such roles carry no moderator permissions. */
  selfAssignable: boolean;
  /** Shown when picking roles, e.g. "Pings for game nights". */
  description: string | null;
  /** An emoji shown next to it when picking roles. */
  emoji: string | null;
}

/** A server's own emoji, written in messages as <:name:id>. */
export interface EmojiDTO {
  id: string;
  serverId: string;
  name: string;
  url: string;
}

export interface CategoryDTO {
  id: string;
  serverId: string;
  name: string;
  position: number;
}

export interface ChannelDTO {
  id: string;
  serverId: string;
  categoryId: string | null;
  name: string;
  type: ChannelKind;
  topic: string | null;
  position: number;
  lastMessageId: string | null;
  lastMessageAt: string | null;
  createdAt: string;
  /** Slow mode: seconds members wait between messages (0 = off). */
  slowModeSeconds: number;
  /** Per-role permission overrides; a default-role override denying View Channel makes it private. */
  overwrites: ChannelOverwrite[];
  /** A forum's tags (empty for other channels). */
  forumTags: ForumTagDTO[];
}

/** A label posts in a forum can have, like "Question" or "Bug". */
export interface ForumTagDTO {
  id: string;
  name: string;
  emoji: string | null;
}

export interface ServerMembershipDTO {
  /** Roles on top of the default role, which every member has. */
  roleIds: string[];
  nickname: string | null;
  /** Position in this user's own server list. */
  position: number;
  notificationLevel: NotificationLevel;
  joinedAt: string;
  /** While in the future, you're timed out: you can read but not chat, react or talk. */
  timeoutUntil: string | null;
  /** When you last accepted the server's rules (see ServerDTO.rules). */
  rulesAcceptedAt: string | null;
  /** Your profile just for this server (a Blaze perk; kept, but not shown, without Blaze). */
  profile: MemberProfileDTO | null;
}

/** A profile just for one server, shown instead of someone's own there. */
export interface MemberProfileDTO {
  avatarUrl: string | null;
  bannerUrl: string | null;
  bio: string | null;
}

export interface ServerDTO {
  id: string;
  name: string;
  iconUrl: string | null;
  description: string | null;
  ownerId: string;
  createdAt: string;
  memberCount: number;
  channels: ChannelDTO[];
  categories: CategoryDTO[];
  roles: RoleDTO[];
  emojis: EmojiDTO[];
  /** Rules members accept before they can chat; null when the server has none. */
  rules: string | null;
  /** When members were last asked to accept them. */
  rulesUpdatedAt: string | null;
  /** What new members need before they can chat here. */
  safetyLevel: SafetyLevel;
  /** Pause invites by itself when lots of people join at once. */
  raidProtection: boolean;
  /** While in the future, invites don't let anyone new in. */
  invitesPausedUntil: string | null;
  /** Banner across the top of the channel list (shown from the Spark level). */
  bannerUrl: string | null;
  /** Stokes from Blaze members; they set the server's level (see serverPerks). */
  stokeCount: number;
  me: ServerMembershipDTO;
}

export interface MemberDTO {
  serverId: string;
  userId: string;
  /** Roles on top of the default role, which every member has. */
  roleIds: string[];
  nickname: string | null;
  joinedAt: string;
  /** When their timeout ends (null, or in the past: not timed out). */
  timeoutUntil: string | null;
  /** Their profile for this server, while they have Blaze. */
  profile: MemberProfileDTO | null;
  user: PublicUser;
  presence: PresenceStatus;
}

export interface BanDTO {
  userId: string;
  user: PublicUser;
  reason: string | null;
  createdAt: string;
  bannedBy: PublicUser | null;
}

/** Something done in a server that its audit log records. */
export type AuditAction =
  | 'SERVER_UPDATE'
  | 'SERVER_TRANSFER'
  | 'CHANNEL_CREATE'
  | 'CHANNEL_UPDATE'
  | 'CHANNEL_DELETE'
  | 'CHANNEL_PERMISSIONS'
  | 'CATEGORY_CREATE'
  | 'CATEGORY_UPDATE'
  | 'CATEGORY_DELETE'
  | 'ROLE_CREATE'
  | 'ROLE_UPDATE'
  | 'ROLE_DELETE'
  | 'MEMBER_ROLE_ADD'
  | 'MEMBER_ROLE_REMOVE'
  | 'MEMBER_NICKNAME'
  | 'MEMBER_KICK'
  | 'MEMBER_BAN'
  | 'MEMBER_UNBAN'
  | 'MEMBER_TIMEOUT'
  | 'MEMBER_TIMEOUT_REMOVE'
  | 'MESSAGE_DELETE'
  | 'MESSAGE_PIN'
  | 'MESSAGE_UNPIN'
  | 'INVITE_CREATE'
  | 'INVITE_DELETE'
  | 'EMOJI_CREATE'
  | 'EMOJI_UPDATE'
  | 'EMOJI_DELETE'
  | 'THREAD_UPDATE'
  | 'THREAD_DELETE'
  | 'AUTOMOD_BLOCK'
  | 'AUTOMOD_UPDATE'
  | 'EVENT_CREATE'
  | 'EVENT_UPDATE'
  | 'EVENT_DELETE'
  | 'SAFETY_UPDATE'
  | 'INVITES_PAUSE'
  | 'INVITES_RESUME'
  | 'RAID_DETECTED';

export type AuditTargetType = 'SERVER' | 'USER' | 'CHANNEL' | 'CATEGORY' | 'ROLE' | 'INVITE' | 'EMOJI' | 'THREAD' | 'EVENT';
export type AuditValue = string | number | boolean | null;

/** One thing that changed: `key` is the setting (name, topic, permissions…). */
export interface AuditChange {
  key: string;
  before?: AuditValue;
  after?: AuditValue;
}

export interface AuditLogEntryDTO {
  id: string;
  action: AuditAction;
  actor: PublicUser | null;
  /** What it was done to. The name is from that moment, so it still reads right after a rename or delete. */
  target: { type: AuditTargetType; id: string | null; name: string | null; user: PublicUser | null } | null;
  reason: string | null;
  changes: AuditChange[];
  createdAt: string;
}

/** Something happening in a server, at a time and place, that members can mark as interested. */
export interface ServerEventDTO {
  id: string;
  serverId: string;
  name: string;
  description: string;
  startsAt: string;
  /** Null when no end time was given. */
  endsAt: string | null;
  /** Where it happens in the server (a voice or text channel), or null for somewhere else. */
  channelId: string | null;
  /** Where it happens otherwise (a place or a link). */
  location: string | null;
  createdBy: PublicUser | null;
  interestedCount: number;
  /** Whether you marked it as interested. */
  interested: boolean;
}

/** A server's AutoMod rules (Server Settings → AutoMod). */
export interface AutoModSettingsDTO {
  enabled: boolean;
  /** Words or phrases to block; "*" stands for any letters ("spam*" also blocks "spammer"). */
  blockedWords: string[];
  blockLinks: boolean;
  /** Sites links may still go to when links are blocked, e.g. "youtube.com" (and its subdomains). */
  allowedLinkDomains: string[];
  /** The most people and roles one message may mention; null for no limit. */
  maxMentions: number | null;
  /** Block floods (too many messages too fast) and the same message over and over. */
  blockSpam: boolean;
  /** Time out whoever breaks a rule, for this many seconds; 0 just blocks the message. */
  timeoutSeconds: number;
  /** Roles AutoMod leaves alone. People who can manage messages are always left alone. */
  exemptRoleIds: string[];
}

export interface AuditLogPageDTO {
  entries: AuditLogEntryDTO[];
  nextCursor: string | null;
}

export interface InviteDTO {
  code: string;
  serverId: string;
  creator: PublicUser | null;
  maxUses: number | null;
  uses: number;
  expiresAt: string | null;
  createdAt: string;
  /** The server's custom invite link (one per server, chosen by its managers; never expires). */
  vanity: boolean;
}

export interface InvitePreviewDTO {
  code: string;
  server: { id: string; name: string; iconUrl: string | null; description: string | null };
  memberCount: number;
  onlineCount: number;
  inviter: PublicUser | null;
  alreadyMember: boolean;
  expiresAt: string | null;
  /** The server has paused invites for now (raid protection, or its managers did). */
  paused: boolean;
}

export interface AttachmentDTO {
  id: string;
  fileName: string;
  url: string;
  contentType: string;
  size: number;
  width: number | null;
  height: number | null;
  /** Voice messages: how long it is. Null for any other file. */
  durationMs: number | null;
  /** Voice messages: loudness over time, as base64 bytes (0–255), for drawing the waveform. */
  waveform: string | null;
  /** Shown blurred until someone chooses to see it (missing on messages forwarded before this existed). */
  spoiler?: boolean;
  /** What the image or video shows, for screen readers (alt text). */
  description?: string | null;
}

/** A message someone saved for later, optionally with a reminder. */
export interface SavedMessageDTO {
  messageId: string;
  /** When we'll remind them (null: just saved, or already reminded). */
  remindAt: string | null;
  savedAt: string;
  message: MessageDTO;
  where:
    /** In a thread, channelId is the thread's and threadParentId its channel's. */
    | { kind: 'channel'; serverId: string; serverName: string; channelId: string; channelName: string; threadParentId: string | null }
    | { kind: 'conversation'; conversationId: string; name: string };
}

/** What a message said before one of its edits. */
export interface MessageVersionDTO {
  content: string;
  /** When this version was written. */
  writtenAt: string;
}

export interface ReactionDTO {
  emoji: string;
  count: number;
  userIds: string[];
}

export interface MessageReferenceDTO {
  id: string;
  author: PublicUser | null;
  content: string;
  hasAttachments: boolean;
}

export interface MessageDTO {
  id: string;
  type: MessageKind;
  channelId: string | null;
  conversationId: string | null;
  serverId: string | null;
  author: PublicUser | null;
  content: string;
  createdAt: string;
  editedAt: string | null;
  replyToId: string | null;
  replyTo: MessageReferenceDTO | null;
  /** False when the author replied without pinging the replied-to user. */
  replyPing: boolean;
  pinnedAt: string | null;
  mentions: string[];
  /** Roles the message pinged. */
  mentionRoleIds: string[];
  /** Whether "@everyone" in it pinged everyone who can see the channel. */
  mentionEveryone: boolean;
  attachments: AttachmentDTO[];
  reactions: ReactionDTO[];
  poll: PollDTO | null;
  /** The thread started from this message, if any. */
  thread: ThreadDTO | null;
  /** A message forwarded here: a copy of it as it was (content is the forwarder's own comment). */
  forwarded: ForwardedMessageDTO | null;
  /** Pictures for custom emoji from other servers, so everyone can see them (sent with Blaze). */
  emojiUrls: Record<string, string> | null;
  /** Echoed back to the sender so optimistic messages can be reconciled. */
  nonce?: string | null;
}

/** A copy of a forwarded message, kept as it was when it was forwarded. */
export interface ForwardedMessageDTO {
  messageId: string;
  author: { id: string | null; displayName: string; avatarUrl: string | null };
  content: string;
  createdAt: string;
  attachments: AttachmentDTO[];
  /** Where it came from ("#general · ClanFire Official", or a DM). */
  from: string;
}

/**
 * A side conversation inside a text channel. Its messages use the thread's id
 * as their channelId; whoever can see the parent channel can see the thread.
 */
export interface ThreadDTO {
  id: string;
  serverId: string;
  /** The text channel it's in. */
  parentId: string;
  name: string;
  /** Who started it. */
  ownerId: string | null;
  /** The message it was started from (null once that message is deleted). */
  starterMessageId: string | null;
  messageCount: number;
  lastMessageId: string | null;
  lastMessageAt: string | null;
  /** Archived threads are listed apart; sending a message reopens them. */
  archivedAt: string | null;
  createdAt: string;
  /** A forum post's tags (ids from its forum's forumTags). */
  tagIds: string[];
}

/** A forum post: a thread, with a peek at its first message. */
export interface ForumPostDTO extends ThreadDTO {
  firstMessage: { content: string; author: PublicUser | null; attachmentCount: number } | null;
}

export interface ForumPostListDTO {
  /** Most recently active first. */
  posts: ForumPostDTO[];
  readStates: Array<{ channelId: string; lastReadAt: string }>;
}

export interface ThreadListDTO {
  /** The channel's threads, most recently active first. */
  threads: ThreadDTO[];
  /** When you last read each of them (missing: never opened). */
  readStates: Array<{ channelId: string; lastReadAt: string }>;
}

export interface PollDTO {
  id: string;
  question: string;
  allowMultiple: boolean;
  /** When voting closes (null: open until the author ends it). */
  endsAt: string | null;
  options: Array<{ id: string; text: string; voterIds: string[] }>;
}

export interface MessagePageDTO {
  messages: MessageDTO[];
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
}

export interface ConversationDTO {
  id: string;
  type: ConversationKind;
  /** Everyone in the conversation except you (the other person, in a DM). */
  recipients: PublicUser[];
  /** Groups: the name given to the group (null: named after its members). */
  name: string | null;
  /** Groups: the owner, who can remove members. */
  ownerId: string | null;
  lastMessageId: string | null;
  lastMessageAt: string | null;
  lastReadAt: string;
  unreadCount: number;
  createdAt: string;
}

export interface FriendDTO {
  user: PublicUser;
  since: string;
}

export interface FriendRequestDTO {
  id: string;
  user: PublicUser;
  direction: 'incoming' | 'outgoing';
  createdAt: string;
}

export interface FriendsDTO {
  friends: FriendDTO[];
  incoming: FriendRequestDTO[];
  outgoing: FriendRequestDTO[];
}

/** 'blocked' = the viewer has blocked this user. */
export type RelationshipStatus = 'none' | 'friends' | 'incoming' | 'outgoing' | 'self' | 'blocked';

export interface UserProfileDTO {
  user: PublicUser;
  presence: PresenceStatus;
  relationship: RelationshipStatus;
  friendRequestId: string | null;
  mutualServers: Array<{ id: string; name: string; iconUrl: string | null }>;
  mutualFriendCount: number;
}

export interface NotificationDTO {
  id: string;
  type: NotificationKind;
  actor: PublicUser | null;
  serverId: string | null;
  serverName: string | null;
  channelId: string | null;
  channelName: string | null;
  conversationId: string | null;
  messageId: string | null;
  preview: string | null;
  /** For EVENT_REMINDER: the event that's about to start. */
  event: { id: string; name: string; startsAt: string } | null;
  readAt: string | null;
  createdAt: string;
}

export interface ReadStateDTO {
  channelId: string;
  lastReadAt: string;
  mentionCount: number;
  /** Muted: no unread dot (mentions still count). */
  muted: boolean;
}

export interface VoiceParticipantDTO {
  userId: string;
  channelId: string;
  serverId: string;
  muted: boolean;
  deafened: boolean;
  speaking: boolean;
  /** Camera is on. */
  video: boolean;
  /** Sharing their screen. */
  screen: boolean;
}

export interface IceServerConfig {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export interface ClientConfigDTO {
  iceServers: IceServerConfig[];
  /** VAPID public key for Web Push, or null when push is unavailable. */
  pushPublicKey: string | null;
  maxUploadBytes: number;
  allowedUploadTypes: string[];
  /** Whether sign-up needs an invite, and which staff can create them. */
  registration: RegistrationMode;
  inviteMinRank: 'CEO' | 'ADMIN' | 'MODERATOR';
  emailVerification: EmailVerificationMode;
  /** The KLIPY key for the GIF picker (searches go straight from the browser), or null when GIFs are off. */
  gifApiKey: string | null;
}

export type RegistrationMode = 'open' | 'invite';
export type EmailVerificationMode = 'off' | 'optional' | 'required';

/** What the sign-up page needs to know before anyone is logged in. */
export interface PublicAuthConfigDTO {
  registration: RegistrationMode;
  emailVerification: EmailVerificationMode;
  /** Sign-in providers this server has set up (others aren't offered). */
  oauthProviders: OAuthProviderId[];
  /** Cloudflare Turnstile's site key when sign-up, login and password resets check for bots; null when off. */
  turnstileSiteKey: string | null;
}

/** Sign in with another account (OAuth 2.1-style: authorization code + PKCE). */
export type OAuthProviderId = 'google' | 'github';

export const OAUTH_PROVIDER_LABELS: Record<OAuthProviderId, string> = { google: 'Google', github: 'GitHub' };

/** A provider account connected to yours (Settings → Security). */
export interface OAuthAccountDTO {
  provider: OAuthProviderId;
  /** How the provider shows them: a username or name. */
  displayName: string | null;
  email: string | null;
  createdAt: string;
  lastUsedAt: string;
}

export interface SignupInviteDTO {
  id: string;
  code: string;
  /** The full sign-up link to share. */
  url: string;
  note: string;
  maxUses: number | null;
  uses: number;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  status: 'active' | 'used-up' | 'expired' | 'revoked';
  createdBy: PublicUser | null;
  /** Accounts that signed up with it. */
  joined: PublicUser[];
}

export interface BootstrapDTO {
  user: SelfUser;
  settings: UserSettingsDTO;
  servers: ServerDTO[];
  conversations: ConversationDTO[];
  readStates: ReadStateDTO[];
  presences: Record<string, PresenceStatus>;
  voice: VoiceParticipantDTO[];
  unreadNotificationCount: number;
  pendingFriendRequestCount: number;
  blockedUserIds: string[];
  /** Messages you've saved (and any reminders), so they show as saved everywhere. */
  saved: Array<{ messageId: string; remindAt: string | null }>;
  /** The welcome tour and the getting-started checklist for new accounts. */
  gettingStarted: GettingStartedDTO;
  config: ClientConfigDTO;
}

export interface LinkPreviewDTO {
  url: string;
  siteName: string | null;
  title: string | null;
  description: string | null;
  /** Proxied through our server (never hot-linked). */
  imageUrl: string | null;
  themeColor: string | null;
}

export interface SearchResultDTO {
  total: number;
  results: Array<{
    message: MessageDTO;
    channelName: string | null;
    serverName: string | null;
  }>;
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    fields?: Record<string, string>;
  };
}

/* ------------------------------------------------------------------ */
/* Staff panel                                                         */
/* ------------------------------------------------------------------ */

export interface StaffStatsDTO {
  users: number;
  newUsers7d: number;
  onlineNow: number;
  servers: number;
  messages: number;
  messages24h: number;
  suspended: number;
  staff: number;
  openReports: number;
}

export interface StaffUserDTO {
  user: PublicUser;
  /** Only visible to Admins and the CEO. */
  email: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  online: boolean;
  serverCount: number;
  suspendedAt: string | null;
  suspendedReason: string | null;
  /** ClanFire Blaze runs until then (null: none). */
  blazeUntil: string | null;
}

export interface StaffUserPageDTO {
  users: StaffUserDTO[];
  nextCursor: string | null;
}

/** Text channels of the official server, for posting as the official account (Staff panel, CEO only). */
export interface OfficialChannelsDTO {
  serverId: string;
  channels: Array<{ id: string; name: string }>;
}

/** A post the CEO scheduled as the official account. */
export interface ScheduledPostDTO {
  id: string;
  channelId: string;
  channelName: string;
  content: string;
  sendAt: string;
  createdBy: PublicUser | null;
}

/** The official account's recent posts (to edit or delete) and what's scheduled. */
export interface OfficialPostsDTO {
  posts: Array<{ message: MessageDTO; channelName: string }>;
  scheduled: ScheduledPostDTO[];
}

export interface StaffActionDTO {
  id: string;
  action:
    | 'SUSPEND'
    | 'UNSUSPEND'
    | 'SET_RANK'
    | 'SET_BADGE'
    | 'RESET_TWO_FACTOR'
    | 'REPORT_RESOLVED'
    | 'REPORT_DISMISSED'
    | 'INVITE_CREATED'
    | 'INVITE_REVOKED'
    | 'OFFICIAL_POST'
    | 'OFFICIAL_EDIT'
    | 'OFFICIAL_DELETE'
    | 'OFFICIAL_SCHEDULE'
    | 'GIVE_BLAZE'
    | 'REMOVE_BLAZE'
    | 'BLAZE_SALE';
  detail: string;
  actor: PublicUser | null;
  target: PublicUser | null;
  createdAt: string;
}


export interface ReportDTO {
  id: string;
  reason: ReportReason;
  details: string;
  status: ReportStatus;
  /** Where it happened, e.g. "Pixel Guild › #general" or "Direct message". */
  context: string;
  /** The message as it was when reported (null for account reports). */
  messageSnapshot: string | null;
  /** Set while the reported message still exists. */
  messageId: string | null;
  reporter: PublicUser | null;
  target: PublicUser | null;
  handledBy: PublicUser | null;
  handledAt: string | null;
  resolution: string;
  createdAt: string;
}

export interface ReportPageDTO {
  reports: ReportDTO[];
  nextCursor: string | null;
}

/* Getting started --------------------------------------------------- */

export type GettingStartedStep = 'avatar' | 'twoFactor' | 'message' | 'voice' | 'friend' | 'server';

export interface GettingStartedDTO {
  /** Open the welcome tour (new accounts that haven't seen it). */
  showTour: boolean;
  /** Show the checklist (new accounts that haven't hidden it). */
  showChecklist: boolean;
  steps: Record<GettingStartedStep, boolean>;
}

/* Feedback ----------------------------------------------------------- */

export type FeedbackKind = 'bug' | 'idea' | 'other';

/* Soundboard --------------------------------------------------------- */

/** A server's own soundboard sound. */
export interface ServerSoundDTO {
  id: string;
  serverId: string;
  name: string;
  emoji: string | null;
  url: string;
  createdAt: string;
}

/** A sound played in a voice channel: a built-in one (url null) or the server's own. */
export interface SoundPlayDTO {
  id: string;
  name: string;
  emoji: string | null;
  url: string | null;
}

/** Sounds every soundboard has. They're made in the browser, so there's nothing to download. */
export const BUILTIN_SOUNDS = [
  { id: 'builtin:airhorn', name: 'Airhorn', emoji: '📯' },
  { id: 'builtin:rimshot', name: 'Ba dum tss', emoji: '🥁' },
  { id: 'builtin:tada', name: 'Ta-da', emoji: '🎉' },
  { id: 'builtin:sad', name: 'Sad trombone', emoji: '🎺' },
  { id: 'builtin:boing', name: 'Boing', emoji: '🦘' },
  { id: 'builtin:crickets', name: 'Crickets', emoji: '🦗' },
  { id: 'builtin:applause', name: 'Applause', emoji: '👏' },
  { id: 'builtin:drumroll', name: 'Drumroll', emoji: '🪘' },
] as const;
export type BuiltinSoundId = (typeof BUILTIN_SOUNDS)[number]['id'];
